import { AuthConsumerRenderProps, withAuthContext } from "auth/AuthContext";
import axios, { CancelTokenSource } from "axios";
import DateTimeSegment from "components/consultations/segments/DateTimeSegment";
import TreaterAndInfoSegment from "components/consultations/segments/TreaterAndInfoSegment";
import { DropdownOption, mapToDropdownOptionArray } from "components/final-form/dropdownUtils";
import FormButtonsRow from "components/layout/FormButtonsRow";
import FormContainer from "components/layout/FormContainer";
import FormContent from "components/layout/FormContent";
import FormModalContentContainer from "components/layout/FormModalContentContainer";
import PraxFormGrid from "components/layout/PraxFormGrid";
import StyledErrorMessage from "components/layout/StyledErrorMessage";
import LoaderComponent from "components/LoaderComponent";
import AccompanyingTextSegment from "components/segments/AccompanyingTextSegment";
import {
  AddEntryResponse,
  EntriesConsumerRenderProps,
  ExtendedBaseEntryDto,
  withEntriesContext
} from "components/segments/entires/EntriesContext";
import EntriesSegment from "components/segments/entires/EntriesSegment";
import MedicamentModal from "components/segments/medicaments/MedicamentModal";
import ProcedureModal from "components/segments/procedures/ProcedureModal";
import ProceduresSegment, { Errors } from "components/segments/procedures/ProceduresSegment";
import ReferenceProcedureMastersModal from "components/segments/procedures/ReferenceProcedureMastersModal";
import SpecialProcedureModal from "components/segments/procedures/SpecialProcedureModal";
import TarmedProcedureModal from "components/segments/procedures/TarmedProcedureModal";
import { FormState } from "final-form";
import moment from "moment";
import React, { Component } from "react";
import { Form as FinalForm, FormRenderProps, FormSpy } from "react-final-form";
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Grid, Modal } from "semantic-ui-react";
import { editConsultation, getUpsertConsultationById, saveConsultation } from "services/consultationServices";
import { updateTarmedEntriesWithEmergencyData } from "services/entryServices";
import { getAllReasonsForEncounter } from "services/reasonForEncounterServices";
import { getAllSubCostCentersByCostCenterId } from "services/subCostCenterServices";
import { getAllActiveTreaters } from "services/treaterServices";
import { debounce } from "ts-debounce";
import { EntryTable, ProcedureCategory } from "ts-types/api.enums";
import {
  BaseEntryDto,
  ConsultationDto,
  ConsultationEntryDto,
  EmergencyDataRequest,
  EmergencyType,
  PatientDto,
  SearchProceduresRequest,
  SearchProceduresResult,
  TreatmentDto,
  UpsertConsultationDto
} from "ts-types/api.types";
import { IndexSignature } from "utils/praxTypes";
import { isReferenceProcedure } from "utils/tarmedUtils";
import {
  formatTarmedErrorMessage,
  formatTarmedErrorMessages,
  TarmedErrorMessage,
  timeValidator
} from "utils/validatorUtils";

const leftSegmentStyle = {
  width: "45%",
  paddingRight: "1rem"
};

const rightSegmentStyle = {
  width: "53%",
  paddingLeft: "1rem"
};

export interface QuantityValidationData {
  consultationId?: number;
  treatmentId?: number;
  patientId?: number;
  validDate: string;
  tariffId: number;
  electronicDataTransfer: boolean;
}

interface IndexedSearchData extends SearchProceduresRequest, IndexSignature {
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, EntriesConsumerRenderProps {
  consultationFormOpen: boolean,
  isCostEstimationComplete: boolean,
  onCloseUpsertModal: () => void,
  currentTreatment: TreatmentDto,
  currentPatient: PatientDto
  consultationId?: number,
  addConsultation: (consultation: ConsultationDto) => void,
  editConsultation: (consultation: ConsultationDto) => void,
  setCacheSearchId: (searchId?: string) => void
}

interface State {
  treaters: Array<DropdownOption>,
  subCostCenters: Array<DropdownOption>
  reasonsForEncounter: Array<DropdownOption>,
  emergencies: Array<DropdownOption>,
  searchData: Partial<IndexedSearchData>,
  loadingData: boolean,
  consultation?: UpsertConsultationDto,
  errorMessages?: Array<string>,
  accompanyingTextModalOpen: boolean
  accompanyingText: string
  editTarmedProcedureModalOpen: boolean,
  specialProcedureModalOpen: boolean
  editEntry?: ExtendedBaseEntryDto,
  // descendant (e.g. a reference procedure) that is pre-selected in the tarmed procedure modal
  preselectedChildCode?: string,
  // reference procedure with multiple master procedures for which the master has to be chosen
  referenceProcedure?: ExtendedBaseEntryDto,
  referenceMastersModalOpen: boolean,
  editProcedureModalOpen: boolean,
  editMedicamentModalOpen: boolean,
  emergencyProcedure?: BaseEntryDto,
  emergencyBastardProcedure?: BaseEntryDto,
  quantityValidationData: QuantityValidationData,
  cancelTokenSource: CancelTokenSource
}

const emergencies: Array<EmergencyType> = ['NO_EMERGENCY', 'VISIT', 'PHONE', 'BIRTH'];

class UpsertConsultation extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const {
      getMessage,
      currentTreatment,
      currentPatient,
      currentUser,
      setCurrentEntries,
      consultationId,
      options: {workDate}
    } = this.props;

    const emergencyOptions: Array<DropdownOption> = emergencies.map(emergency => {
      return {
        key: emergency,
        text: getMessage(`consultation.${emergency.toLowerCase().replace("_", "")}`),
        value: emergency
      }
    });

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      treaters: [],
      subCostCenters: [],
      reasonsForEncounter: [],
      emergencies: emergencyOptions,
      loadingData: true,
      searchData: {
        emergencyData: {
          emergencyType: "NO_EMERGENCY",
          holiday: false
        },
        validDate: currentTreatment.treatmentDate,
        tariffId: currentTreatment!.tariffId,
        patientId: currentPatient!.id,
        mandateId: currentUser!.userConfig.mandateId,
        costEstimationId: currentTreatment.costEstimationId,
        treatmentId: currentTreatment.id
      },
      accompanyingTextModalOpen: false,
      accompanyingText: "",
      editTarmedProcedureModalOpen: false,
      referenceMastersModalOpen: false,
      editProcedureModalOpen: false,
      editMedicamentModalOpen: false,
      specialProcedureModalOpen: false,
      quantityValidationData: {
        consultationId: consultationId,
        treatmentId: currentTreatment.id,
        patientId: currentPatient.id,
        validDate: currentTreatment.treatmentDate
          || moment().format("YYYY-MM-DD"),
        tariffId: currentTreatment.tariffId,
        electronicDataTransfer: currentTreatment.electronicDataTransfer
      },
      cancelTokenSource
    };

    let methods: Array<any> = [
      getAllActiveTreaters(cancelTokenSource),
      getAllSubCostCentersByCostCenterId(1, cancelTokenSource),
      getAllReasonsForEncounter(cancelTokenSource)
    ];

    if (consultationId) {
      methods = [...methods, getUpsertConsultationById(consultationId, cancelTokenSource)];
    }

    const onFinally = () => {
      this.setState({
        loadingData: false
      })
    };

    Promise.all(methods)
        .then(responses => {

          const consultation: UpsertConsultationDto = responses[3] ? responses[3] : undefined;

          if (consultation) {
            setCurrentEntries(consultation.consultationEntries);
          }

          this.setState({
            treaters: mapToDropdownOptionArray(responses[0], ["name", "firstName"]),
            // TODO ftrajkov 2019-06-13: Use another mapper to get `CostCenter.description | SubCostCenter.description`
            subCostCenters: mapToDropdownOptionArray(responses[1], "description"),
            reasonsForEncounter: mapToDropdownOptionArray(responses[2], "description"),
            consultation: consultation,
            accompanyingText: consultation && consultation.specialText ? consultation.specialText : ""
          })
        })
        .catch(this.onCloseModal)
        .finally(onFinally);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  initialConsultationValues = (): Partial<UpsertConsultationDto> => {

    const {
      treaters,
      subCostCenters,
      searchData,
      quantityValidationData
    } = this.state;

    const {
      options,
      currentUser,
      currentTreatment
    } = this.props;

    // Update holiday check box for Saturday and Sunday
    const holidayDaysOfWeek = [6, 7];
    const currentDayOfWeek = moment(options.workDate).day();
    const isHoliday = holidayDaysOfWeek.includes(currentDayOfWeek);

    return {
      emergencyType: searchData.emergencyData ? searchData.emergencyData.emergencyType : "NO_EMERGENCY",
      treaterId: treaters ? options.currentTreater.id : undefined,
      subCostCenterId: subCostCenters ? subCostCenters[0].value : undefined,
      consultationDate: quantityValidationData.validDate,
      consultationTime: searchData.validTime ? searchData.validTime : moment().format("HH:mm"),
      treatmentId: currentTreatment.id,
      userId: currentUser!.id,
      bmiIndex: false,
      holiday: isHoliday
    };
  };

  toggleAccompanyingTextModal = () => {
    this.setState(({accompanyingTextModalOpen}: State) => ({
      accompanyingTextModalOpen: !accompanyingTextModalOpen
    }));
  };

  onSaveAccompanyingText = (text: string) => {
    this.setState({
      accompanyingText: text
    });

    this.toggleAccompanyingTextModal();
  };

  toggleEditTarmedProcedureModal = () => {
    this.setState(({editTarmedProcedureModalOpen}: State) => ({
      editTarmedProcedureModalOpen: !editTarmedProcedureModalOpen
    }))
  };

  toggleSpecialProcedureModalOpen = () => {
    this.setState(({specialProcedureModalOpen}: State) => ({
      specialProcedureModalOpen: !specialProcedureModalOpen
    }))
  };

  toggleEditProcedureModal = () => {
    this.setState(({editProcedureModalOpen}: State) => ({
      editProcedureModalOpen: !editProcedureModalOpen
    }))
  };

  toggleEditMedicamentModal = () => {
    this.setState(({editMedicamentModalOpen}: State) => ({
      editMedicamentModalOpen: !editMedicamentModalOpen
    }))
  };

  onUpsertTarmedProcedure = (tarmedProcedure: ExtendedBaseEntryDto, preselectedChildCode?: string) => {

    // a reference procedure can only be entered as a descendant of one of its master procedures
    if (isReferenceProcedure(tarmedProcedure) && tarmedProcedure.masters && tarmedProcedure.masters.length > 0) {
      this.handleReferenceProcedure(tarmedProcedure);
      return;
    }

    this.setState({
      editEntry: tarmedProcedure,
      preselectedChildCode: preselectedChildCode
    });

    this.toggleEditTarmedProcedureModal();
  };

  handleReferenceProcedure = (referenceProcedure: ExtendedBaseEntryDto) => {

    const masters = referenceProcedure.masters!;

    if (masters.length == 1) {
      this.openMasterProcedure(masters[0], referenceProcedure);
    } else {
      this.setState({
        referenceProcedure: referenceProcedure,
        referenceMastersModalOpen: true
      });
    }
  };

  closeReferenceMastersModal = () => {
    this.setState({
      referenceProcedure: undefined,
      referenceMastersModalOpen: false
    });
  };

  findMasterEntry = (master: BaseEntryDto): ExtendedBaseEntryDto | undefined => {
    const {currentEntries} = this.props;
    return currentEntries.find(entry => entry.tarmedProcedure && entry.code == master.code);
  };

  isMasterPresent = (master: BaseEntryDto): boolean => {
    return !!this.findMasterEntry(master);
  };

  onSelectReferenceMaster = (master: BaseEntryDto) => {

    const {referenceProcedure} = this.state;

    this.closeReferenceMastersModal();

    if (referenceProcedure) {
      this.openMasterProcedure(master, referenceProcedure);
    }
  };

  /**
   * Opens the edit dialog for the master procedure if it is already part of the consultation,
   * otherwise the master procedure is added through the regular add flow (including all validations).
   */
  openMasterProcedure = (master: BaseEntryDto, referenceProcedure: ExtendedBaseEntryDto) => {

    const existingMaster = this.findMasterEntry(master);

    if (existingMaster) {
      this.onUpsertTarmedProcedure(existingMaster, referenceProcedure.code);
    } else {
      this.addMasterProcedure(master, referenceProcedure);
    }
  };

  addMasterProcedure = (master: BaseEntryDto, referenceProcedure: ExtendedBaseEntryDto) => {

    const {
      addEntry,
      editEntry,
      consultationId,
      currentTreatment,
      getMessage,
      getMessageWithNamedParams
    } = this.props;

    const {searchData} = this.state;

    const procedure: Partial<SearchProceduresResult> = {
      id: master.procedureId,
      code: master.code,
      description: master.description
    };

    const result = addEntry(
        procedure,
        ProcedureCategory.PROCEDURE,
        consultationId,
        currentTreatment.electronicDataTransfer,
        searchData,
        EntryTable.CONSULTATION
    );

    if (!result) {
      return;
    }

    if ((result as Promise<AddEntryResponse | undefined>).then) {
      (result as Promise<AddEntryResponse | undefined>)
          .then(response => {

            if (response && response.entry) {
              // the master requires additional input: open the add dialog
              this.onUpsertTarmedProcedure(response.entry, referenceProcedure.code);
            } else if (response && response.errorMessages && response.errorMessages.length > 0) {
              this.setErrorMessages(
                  formatTarmedErrorMessages(response.errorMessages, getMessage, getMessageWithNamedParams));
            } else {
              // the master was added directly: open the edit dialog for the new consultation entry
              const addedMaster = editEntry(master as ExtendedBaseEntryDto);

              if (addedMaster) {
                this.onUpsertTarmedProcedure(addedMaster, referenceProcedure.code);
              }
            }
          })
          .catch(err => {

            const data = err && err.response ? err.response.data : undefined;
            const violation = data && data.violations ? data.violations[0] : undefined;

            if (violation && violation.errorCode == Errors.PROCEDURE_NOT_VALID_AFTER) {
              this.setErrorMessage(violation.details);
            } else {
              this.setErrorMessage(getMessage("error.general"));
            }
          });
    } else {
      this.setErrorMessage(formatTarmedErrorMessage(result as TarmedErrorMessage, getMessage, getMessageWithNamedParams));
    }
  };

  onEditProcedure = (procedure: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: procedure
    });

    this.toggleEditProcedureModal();
  };

  onEditMedicament = (medicament: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: medicament
    });

    this.toggleEditMedicamentModal();
  };

  onUpsertSpecialProcedure = (specialProcedure?: ExtendedBaseEntryDto) => {

    this.setState({
      editEntry: specialProcedure
    });

    this.toggleSpecialProcedureModalOpen();
  };

  isValid = (key: string, value: string): boolean => {
    if (key === "consultationTime") {
      return !timeValidator(value);
    } else if (key === "consultationDate") {
      return !this.isAfterDate(value);
    } else {
      return true;
    }
  };

  isAfterDate = (value: string) => {
    const {currentTreatment} = this.props;
    const date = moment(value, "YYYY-MM-DD");
    const tDate = moment(currentTreatment.treatmentDate, "YYYY-MM-DD");
    if (date.isValid() && date.isSameOrAfter(tDate)) {
      return undefined;
    }
    return "Invalid date";
  }

  onCloseModal = () => {

    const {setCurrentEntries, resetEmergencyData, onCloseUpsertModal} = this.props;

    resetEmergencyData();
    setCurrentEntries([]);
    onCloseUpsertModal();
  };

  isEmergencyDataValid = (values: Partial<UpsertConsultationDto>): boolean => {
    return values.consultationDate != null
        && values.consultationTime != null
        && values.holiday != null
        && values.emergencyType != null;
  };

  handleChange = debounce(async ({values, error, errors}: FormState) => {

    if (!this.mounted) {
      return;
    }

    const {currentTreatment, currentPatient, updateEmergencyData} = this.props;
    const {currentUser, getTarmedEntries, setLoadingEmergencyData, currentEntries} = this.props;
    const {searchData, quantityValidationData, cancelTokenSource} = this.state;
    const valueKeysToCheck = ["consultationDate", "consultationTime", "holiday", "emergencyType"];
    const searchDataKeysToCheck = ["validDate", "validTime", "emergencyData.holiday", "emergencyData.emergencyType"];

    let changedSearchDataValues: Partial<IndexedSearchData> = {};

    let shouldUpdate = false;
    for (let i = 0; i < valueKeysToCheck.length; i++) {

      const searchDataKey = searchDataKeysToCheck[i];
      const searchDataValue = searchData[searchDataKey];

      const valueKey = valueKeysToCheck[i];
      const formDataValue = values[valueKey];

      if (searchDataValue != formDataValue
        && this.isValid(valueKey, formDataValue)) {

        changedSearchDataValues[searchDataKey] = formDataValue;
        shouldUpdate = true;
      }
    }

    if (shouldUpdate) {

      let changedQuantityValidationData = {...quantityValidationData};
      if (quantityValidationData.validDate != values.consultationDate) {
        changedQuantityValidationData.validDate = values.consultationDate;
      }

      changedSearchDataValues = {
        ...searchData,
        ...changedSearchDataValues
      };

      if (currentTreatment.tarmedTariff && this.isEmergencyDataValid(values)) {

        if (currentEntries.length != 0) {
          setLoadingEmergencyData(true);
        }

        const mandateId = currentUser && currentUser.userConfig.mandateId
            ? currentUser.userConfig.mandateId
            : currentTreatment.mandateId;

        const emergencyDataRequest: EmergencyDataRequest = {
          consultationDate: values.consultationDate,
          consultationTime: values.consultationTime,
          emergencyType: values.emergencyType,
          holiday: values.holiday,
          treatmentId: currentTreatment.id,
          patientId: currentPatient.id,
          tariffId: currentTreatment.tariffId,
          mandateId: mandateId,
          electronicDataTransfer: currentTreatment.electronicDataTransfer,
          consultationDateModified: searchData["validDate"] != values["consultationDate"],
          tarmedEntries: getTarmedEntries()
        };

        await updateTarmedEntriesWithEmergencyData(emergencyDataRequest, cancelTokenSource)
            .then(response => {

              const {emergencyProcedure, emergencyBastardProcedure, updatedTarmedEntries} = response;

              updateEmergencyData(updatedTarmedEntries, emergencyProcedure, emergencyBastardProcedure);
            })
            .finally(() => setLoadingEmergencyData(false))
      }

      await this.setState({
        searchData: changedSearchDataValues,
        quantityValidationData: changedQuantityValidationData
      })
    }
  }, 200);

  handleSubmit = async (values: Partial<UpsertConsultationDto>) => {

    const {
      currentEntries,
      consultationId,
      addConsultation,
      editConsultation: modifyConsultation,
      getMessage
    } = this.props;

    const {accompanyingText} = this.state;

    values.specialText = accompanyingText == "" ? undefined : accompanyingText;

    let consultationEntries: Array<ConsultationEntryDto> = [];

    const mapEntry = (entry: BaseEntryDto): ConsultationEntryDto => {
      return {
        ...entry,
        consultationEntryId: entry.parentEntryId,
        treaterId: values.treaterId!
      }
    };

    currentEntries.forEach(({quantityFromDb, ...currentEntry}: ExtendedBaseEntryDto) => {

      if (currentEntry.additionalEfforts) {
        currentEntry.additionalEfforts.forEach(additionalEffort => additionalEffort.treaterId = values.treaterId!);
      }

      if (currentEntry.bulks) {
        const bulkEntries: Array<ConsultationEntryDto> | undefined = currentEntry.bulks
            .filter(bulk => bulk.quantity > 0 && bulk.code != currentEntry.code)
            .map(bulk => mapEntry(bulk));

        if (bulkEntries != undefined) {
          consultationEntries.push(...bulkEntries);
        }
      }

      if (currentEntry.descendants) {
        const descendantEntries: Array<ConsultationEntryDto> | undefined = currentEntry.descendants
            .filter(descendant => descendant.quantity > 0)
            .map(descendant => mapEntry(descendant));

        if (descendantEntries != undefined) {
          consultationEntries.push(...descendantEntries);
        }
      }

      consultationEntries.push(mapEntry(currentEntry))
    });

    values.consultationEntries = consultationEntries;

    const handleError = () => this.setErrorMessage(getMessage("error.general"));
    const {cancelTokenSource} = this.state;

    if (consultationId) {
      await editConsultation(values, consultationId, cancelTokenSource)
          .then(response => {
            modifyConsultation(response);
            this.onCloseModal();
          })
          .catch(handleError)
    } else {
      await saveConsultation(values, cancelTokenSource)
          .then(response => {
            addConsultation(response);
            this.onCloseModal();
          })
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessages: errorMessage ? [errorMessage] : undefined
    })
  };

  setErrorMessages = (errorMessages?: Array<string>) => {
    this.setState({
      errorMessages: errorMessages
    })
  };

  render(): React.ReactNode {

    const {loadingData} = this.state;
    const {getMessage} = this.props;

    return loadingData
        ? <LoaderComponent
            message={getMessage("consultationForm.loading")}
            dimmerProps={{active: true, inverted: false, page: true}}
            inverted/>
        : this.renderModal();
  }

  renderModal = (): React.ReactNode => {

    const {consultationId, consultationFormOpen, currentTreatment, getMessage} = this.props;
    const {consultation} = this.state;

    return (
        <Modal
            open={consultationFormOpen}
            onClose={this.onCloseModal}
            style={{width: "1500px"}}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            {consultationId ? getMessage('consultation.edit') : getMessage('consultation.add')}
          </Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={this.handleSubmit}
                subscription={{pristine: true, values: true, submitting: true}}
                render={this.renderForm}
                initialValues={consultation ? consultation : this.initialConsultationValues()}
            />
          </FormModalContentContainer>
        </Modal>
    )
  };

  renderForm = ({handleSubmit, submitting, values}: FormRenderProps): React.ReactNode => {

    const {getMessage, currentTreatment, isCostEstimationComplete, consultationId} = this.props;
    const {setCacheSearchId} = this.props;
    const {treaters, subCostCenters, reasonsForEncounter, emergencies, searchData} = this.state;
    const {errorMessages, editTarmedProcedureModalOpen, editEntry, preselectedChildCode} = this.state;
    const {referenceProcedure, referenceMastersModalOpen} = this.state;
    const {editProcedureModalOpen, editMedicamentModalOpen} = this.state;
    const {accompanyingTextModalOpen, accompanyingText} = this.state;
    const {specialProcedureModalOpen, quantityValidationData} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessages
                  && errorMessages.length > 0
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessages()}>
                        {
                          errorMessages.map((errorMessage, index) => {
                            return (
                                <div key={index}>{errorMessage}</div>
                            )
                          })
                        }
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <Grid.Column style={leftSegmentStyle}>
                  <ProceduresSegment
                      searchData={searchData}
                      parentId={consultationId}
                      electronicDataTransfer={currentTreatment.electronicDataTransfer}
                      handleTreatmentClose={() => this.handleSubmit(values)}
                      isCostEstimationComplete={isCostEstimationComplete}
                      onUpsertTarmedProcedure={this.onUpsertTarmedProcedure}
                      onEditProcedure={this.onEditProcedure}
                      onEditMedicament={this.onEditMedicament}
                      onErrors={this.setErrorMessages}
                      onError={this.setErrorMessage}
                      setCacheSearchId={setCacheSearchId}
                      entryTable={EntryTable.CONSULTATION}
                      favoritesEnabled
                  />
                </Grid.Column>
                <Grid.Column style={rightSegmentStyle}>
                  <DateTimeSegment
                      treatmentDate={currentTreatment.treatmentDate}
                      isAfterDate={this.isAfterDate}
                  />
                  <TreaterAndInfoSegment
                      treaters={treaters}
                      subCostCenters={subCostCenters}
                      reasonForEncounters={reasonsForEncounter}
                      emergencies={emergencies}
                  />
                  <EntriesSegment
                      tarmedTariff={currentTreatment!.tarmedTariff}
                      headerText={getMessage("consultation.price")}
                      onUpsertTarmedProcedure={this.onUpsertTarmedProcedure}
                      onUpsertSpecialProcedure={this.onUpsertSpecialProcedure}
                      onEditProcedure={this.onEditProcedure}
                      onEditMedicament={this.onEditMedicament}
                  />
                </Grid.Column>
                <FormSpy subscription={{values: true}} onChange={this.handleChange}/>

                <AccompanyingTextSegment
                    modalOpen={accompanyingTextModalOpen}
                    onCloseModal={this.toggleAccompanyingTextModal}
                    onSaveAction={this.onSaveAccompanyingText}
                    accompanyingText={accompanyingText}
                />
                <TarmedProcedureModal
                    modalOpen={editTarmedProcedureModalOpen}
                    onCloseModal={this.toggleEditTarmedProcedureModal}
                    tarmedProcedure={editTarmedProcedureModalOpen ? editEntry : undefined}
                    tariffId={searchData.tariffId}
                    quantityValidationData={quantityValidationData}
                    entryTable={EntryTable.CONSULTATION}
                    validDate={searchData.validDate}
                    preselectedChildCode={editTarmedProcedureModalOpen ? preselectedChildCode : undefined}
                />
                <ReferenceProcedureMastersModal
                    modalOpen={referenceMastersModalOpen}
                    onCloseModal={this.closeReferenceMastersModal}
                    referenceProcedure={referenceMastersModalOpen ? referenceProcedure : undefined}
                    isMasterPresent={this.isMasterPresent}
                    onSelectMaster={this.onSelectReferenceMaster}
                />
                <ProcedureModal
                    modalOpen={editProcedureModalOpen}
                    onCloseModal={this.toggleEditProcedureModal}
                    procedure={editProcedureModalOpen ? editEntry : undefined}
                />
                {
                    editMedicamentModalOpen &&
                    <MedicamentModal
                        modalOpen={editMedicamentModalOpen}
                        onCloseModal={this.toggleEditMedicamentModal}
                        medicament={editMedicamentModalOpen ? editEntry : undefined}
                    />
                }
                <SpecialProcedureModal
                    modalOpen={specialProcedureModalOpen}
                    onCloseModal={this.toggleSpecialProcedureModalOpen}
                    specialProcedure={specialProcedureModalOpen ? editEntry : undefined}
                />
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={this.onCloseModal}>{getMessage("button.cancel")}</Button>
              <Button compact type="button" floated={"right"} primary disabled={submitting}
                      onClick={this.toggleAccompanyingTextModal}>{getMessage("accompanyingText.short")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
  };
}

const UpsertConsultationWrapper =
    withMessages(
        withAuthContext(
            withEntriesContext(
                UpsertConsultation)));

export default UpsertConsultationWrapper;
